import { supabase } from '@/integrations/supabase/client';

export interface UserRoleData {
  admin: number;
  staff: number;
  user: number;
}

export interface CourseStatusData {
  notStarted: number;
  inProgress: number;
  completed: number;
}

export interface TopCourse {
  name: string;
  enrollments: number;
}

export interface EngagementData {
  date: string;
  activeUsers: number;
}

export const adminChartsDataService = {
  async getUserRoleDistribution(): Promise<UserRoleData> {
    try {
      // Get all user roles
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role, user_id');

      if (error) throw error;

      const distribution = {
        admin: 0,
        staff: 0,
        user: 0,
      };

      roles?.forEach(r => {
        if (r.role === 'admin') distribution.admin++;
        else if (r.role === 'staff') distribution.staff++;
        else distribution.user++;
      });

      // Get all profiles to ensure accurate total count
      const { data: profiles } = await supabase.from('profiles').select('id');
      const profilesWithoutRoles = profiles?.filter(p => 
        !roles?.some(r => r.user_id === p.id)
      ).length || 0;

      distribution.user += profilesWithoutRoles;

      return distribution;
    } catch (error) {
      console.error('Error fetching user role distribution:', error);
      return { admin: 0, staff: 0, user: 0 };
    }
  },

  async getCourseStatusDistribution(): Promise<CourseStatusData> {
    try {
      const { data: assignments, error } = await supabase
        .from('course_assignments')
        .select('status, final_grade, assignment_status');

      if (error) throw error;

      const distribution = {
        notStarted: 0,
        inProgress: 0,
        completed: 0,
      };

      assignments?.forEach(a => {
        // Skip unassigned or removed assignments
        if (a.assignment_status === 'unassigned' || a.assignment_status === 'removed') return;
        
        // Completed: status is 'completed' OR final_grade is 100
        if (a.status === 'completed' || a.final_grade === 100) {
          distribution.completed++;
        }
        // In Progress: has some grade but not completed
        else if (a.final_grade && a.final_grade > 0) {
          distribution.inProgress++;
        }
        // Not Started: assigned but no progress
        else {
          distribution.notStarted++;
        }
      });

      return distribution;
    } catch (error) {
      console.error('Error fetching course status distribution:', error);
      return { notStarted: 0, inProgress: 0, completed: 0 };
    }
  },

  async getTopActiveCourses(limit: number = 5): Promise<TopCourse[]> {
    try {
      const { data: assignments, error } = await supabase
        .from('course_assignments')
        .select('course_id, courses(title)');

      if (error) throw error;

      // Helper to format course ID as readable title
      const formatCourseTitle = (id: string): string => {
        return id.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      };

      // Count enrollments by course
      const courseCounts: Record<string, { title: string; count: number }> = {};

      assignments?.forEach(a => {
        const courseTitle = (a.courses as any)?.title || formatCourseTitle(a.course_id);
        if (!courseCounts[a.course_id]) {
          courseCounts[a.course_id] = { title: courseTitle, count: 0 };
        }
        courseCounts[a.course_id].count++;
      });

      // Convert to array and sort
      const topCourses = Object.values(courseCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, limit)
        .map(c => ({
          name: c.title.length > 30 ? c.title.substring(0, 27) + '...' : c.title,
          enrollments: c.count,
        }));

      return topCourses;
    } catch (error) {
      console.error('Error fetching top active courses:', error);
      return [];
    }
  },

  async getUserEngagementTrend(days: number = 30): Promise<EngagementData[]> {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Use course_assignments as engagement proxy since module_activity_logs may be empty
      const { data: assignments, error } = await supabase
        .from('course_assignments')
        .select('assigned_date, user_id')
        .gte('assigned_date', startDate.toISOString())
        .lte('assigned_date', endDate.toISOString());

      if (error) throw error;

      // Group by date
      const dailyUsers: Record<string, Set<string>> = {};

      assignments?.forEach(assignment => {
        const date = new Date(assignment.assigned_date).toLocaleDateString();
        if (!dailyUsers[date]) {
          dailyUsers[date] = new Set();
        }
        dailyUsers[date].add(assignment.user_id);
      });

      // Generate data for all days (fill gaps with 0)
      const engagementData: EngagementData[] = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toLocaleDateString();
        const shortDate = `${date.getMonth() + 1}/${date.getDate()}`;
        
        engagementData.push({
          date: shortDate,
          activeUsers: dailyUsers[dateStr]?.size || 0,
        });
      }

      return engagementData;
    } catch (error) {
      console.error('Error fetching user engagement trend:', error);
      return [];
    }
  },
};
